import {
  getFilesList,
  getIsPollingForFiles,
  getStagedFiles,
  getFilePreviewsComplete,
  getFileIntervalId,
  getHasFileUploadFailed,
} from './selectors'

import { isDataFile } from './utils'

const BASE_URL = process.env.REACT_APP_GREYHOUND_API2_URL || ''

const formDataFromStagedFile = (stagedFile, userRole) => {
  const formData = new FormData()

  formData.set('file', stagedFile.file)
  formData.set('name', stagedFile.file.name)

  if (!isDataFile(stagedFile.file.name)) {
    formData.set('searchable', stagedFile.isSearchable)
  }

  if (isDataFile(stagedFile.file.name)) {
    if (stagedFile.needsCleaning) {
      formData.set('file_type', 'uncleaned_data')
    } else if (userRole === 'admin') {
      formData.set('file_type', 'data')
    } else if (userRole === 'manager') {
      formData.set('file_type', 'user_data')
    } else {
      formData.set('file_type', 'uncleaned_data')
    }
  }

  const groupList = Object.entries(stagedFile.groupRestrictions).reduce(
    (acc, [group, access]) => {
      if (access) return acc.concat(group)
      return acc
    },
    []
  )

  formData.set('group_restrictions_json', JSON.stringify(groupList))

  return formData
}

export const FETCH_FILES_LOADING = 'FETCH_FILES_LOADING'
const fetchFilesLoading = () => ({ type: FETCH_FILES_LOADING })

export const FETCH_FILES_SUCCESS = 'FETCH_FILES_SUCCESS'
const fetchFilesSuccess = (studyId, files) => ({
  type: FETCH_FILES_SUCCESS,
  studyId,
  files,
})

export const FETCH_FILES_ERROR = 'FETCH_FILES_ERROR'
const fetchFilesError = error => ({
  type: FETCH_FILES_ERROR,
  error,
})

export const SET_SORT_COLUMN = 'SET_SORT_COLUMN'
export const setSortColumn = column => ({
  type: SET_SORT_COLUMN,
  column,
})

export const SET_SORT_ORDER = 'SET_SORT_ORDER'
export const setSortOrder = order => ({
  type: SET_SORT_ORDER,
  order,
})

export const CLEAR_FILE_INTERVAL_ID = 'CLEAR_FILE_INTERVAL_ID'
const __clearFileIntervalId = () => ({ type: CLEAR_FILE_INTERVAL_ID })

export const stopFilePolling = () => (dispatch, getState) => {
  const fileIntervalId = getFileIntervalId(getState())
  clearInterval(fileIntervalId)
  dispatch(__clearFileIntervalId())
}

export const SET_FILE_INTERVAL_ID = 'SET_FILE_INTERVAL_ID'
const __setFileIntervalId = fileIntervalId => ({
  type: SET_FILE_INTERVAL_ID,
  fileIntervalId,
})

const __filePollStatus = studyNumber => async (dispatch, getState, { fetch }) => {
  const response = await fetch(`${BASE_URL}/studies/${studyNumber}/files/`)
  const result = await response.json()
  const resultComplete = !result.filter(file => file.file_type === 'document').some(file => file.preview_state === 'processing' || file.preview_state === 'initialized')
  if (resultComplete) {
    dispatch(fetchFilesSuccess(studyNumber, result))
    dispatch(stopFilePolling())
  }
}

export const startFilePolling = studyNumber => (dispatch, getState) => {
  const isPollingForFiles = getIsPollingForFiles(getState())
  const filePreviewsComplete = getFilePreviewsComplete(getState())
  if (isPollingForFiles || filePreviewsComplete) return

  const fileIntervalId = setInterval(() => {
    dispatch(__filePollStatus(studyNumber))
  }, 5000)

  dispatch(__setFileIntervalId(fileIntervalId))
}

export const fetchFiles = (studyId, params) => async (dispatch, getState, { fetch }) => {
  dispatch(fetchFilesLoading())
  const query = params ? `?${new URLSearchParams(params).toString()}` : ''
  const response = await fetch(`${BASE_URL}/studies/${studyId}/files/${query}`)
  if (!response.ok) {
    dispatch(fetchFilesError(response.statusText))
    return
  }

  const result = await response.json()
  dispatch(fetchFilesSuccess(studyId, result))
}

export const SET_FILES_FILTER_QUERY = 'SET_FILES_FILTER_QUERY'
export const setFilesFilterQuery = filterQuery => ({
  type: SET_FILES_FILTER_QUERY,
  filterQuery,
})

export const STAGE_FILE_FOR_UPLOAD = 'STAGE_FILE_FOR_UPLOAD'
const __stageFileForUpload = (
  file,
  groupRestrictions = {},
  isSearchable = false,
  needsCleaning = false,
) => ({
  type: STAGE_FILE_FOR_UPLOAD,
  file,
  groupRestrictions,
  isSearchable,
  needsCleaning,
})

/**
 * @param {File} file - File to be staged for upload to the study
 * @param {Array<String>} datasetGroupRestrictions - ACLs for datasets belong to Collie,
 * existing ACLs need to be sourced from studyDatasets reducer.
 */
export const stageFileForUpload = (
  file,
  datasetGroupRestrictions = [],
  hasExistingDataset = false,
) => (dispatch, getState) => {
  const existingFiles = getFilesList(getState())
  const replacementTarget = existingFiles.find(ef => ef.name === file.name)

  const needsCleaning = isDataFile(file.name) && hasExistingDataset
  const isSearchable = replacementTarget?.isSearchable ?? !isDataFile(file.name)
  const nextACLs = (() => {
    if (replacementTarget && isDataFile(file.name)) {
      return datasetGroupRestrictions
    }
    return replacementTarget?.groupRestrictions ?? []
  })()
  const nextACLsMap = nextACLs.reduce((acc, group) => {
    acc[group] = true
    return acc
  }, {})

  dispatch(__stageFileForUpload(file, nextACLsMap, isSearchable, needsCleaning))
  window.analytics.track('Stage File For Upload', {
    fileName: file.name,
    isDataFile: isDataFile(file.name),
  })
}

export const UPDATE_SEARCHABLE_FOR_STAGED_FILE = 'UPDATE_SEARCHABLE_FOR_STAGED_FILE'
export const updateSearchableForStagedFile = (fileName, isSearchable) => ({
  type: UPDATE_SEARCHABLE_FOR_STAGED_FILE,
  fileName,
  isSearchable,
})

export const UPDATE_ACLS_FOR_STAGED_FILE = 'UPDATE_ACLS_FOR_STAGED_FILE'
export const updateACLsForStagedFile = (fileName, groupName, isActive) => ({
  type: UPDATE_ACLS_FOR_STAGED_FILE,
  fileName,
  groupName,
  isActive,
})

export const UPDATE_DATA_FILE_CLEANING = 'UPDATE_DATA_FILE_CLEANING'
export const updateDataFileCleaning = (fileName, needsCleaning) => ({
  type: UPDATE_DATA_FILE_CLEANING,
  fileName,
  needsCleaning,
})

export const UNSTAGE_FILE_FOR_UPLOAD = 'UNSTAGE_FILE_FOR_UPLOAD'
export const unstageFileForUpload = fileName => ({
  type: UNSTAGE_FILE_FOR_UPLOAD,
  fileName,
})

export const UNSTAGE_ALL_FILES_FOR_UPLOAD = 'UNSTAGE_ALL_FILES_FOR_UPLOAD'
export const unstageAllFilesForUpload = () => ({ type: UNSTAGE_ALL_FILES_FOR_UPLOAD })

const GENERIC_CREATE_ERROR_MESSAGE = 'Error uploading file, please try again later.'
const processFilePostError = async (stagedFile, response, dispatch, errorAction) => {
  if (response.status < 500) {
    const error = await response.json()
    const errorMessage = (() => {
      if (error?.[0]?.includes('file too large')) {
        if (isDataFile(stagedFile.file.name)) {
          return 'This data file is too large to be loaded automatically, please reach out to your KnowledgeHound Account Team for help.'
        }
        return 'This file is too large to be uploaded, please reach out to your KnowledgeHound Account Team for help.'
      }
      return GENERIC_CREATE_ERROR_MESSAGE
    })()

    dispatch(errorAction(stagedFile.file.name, errorMessage))
  } else {
    dispatch(errorAction(stagedFile.file.name, GENERIC_CREATE_ERROR_MESSAGE))
  }
}

export const CREATE_FILE_LOADING = 'CREATE_FILE_LOADING'
const __createFileLoading = fileName => ({
  type: CREATE_FILE_LOADING,
  fileName,
})

export const CREATE_FILE_SUCCESS = 'CREATE_FILE_SUCCESS'
const __createFileSuccess = (fileName, payload) => ({
  type: CREATE_FILE_SUCCESS,
  fileName,
  payload,
})

export const CREATE_FILE_ERROR = 'CREATE_FILE_ERROR'
const __createFileError = (fileName, error) => ({
  type: CREATE_FILE_ERROR,
  error,
  fileName,
})

export const createFile = (
  studyId,
  stagedFile,
  userRole
) => async (dispatch, getState, { fetch }) => {
  dispatch(__createFileLoading(stagedFile.file.name))

  const formData = formDataFromStagedFile(stagedFile, userRole)
  const response = await fetch(`${BASE_URL}/studies/${studyId}/files/`, {
    method: 'POST',
    body: formData,
  })
  if (!response.ok) {
    await processFilePostError(stagedFile, response, dispatch, __createFileError)
    return null
  }

  if (stagedFile.type === 'DATA') {
    window.analytics.track('Data File Uploaded', {
      studyId,
      dataCleaning: stagedFile.needsCleaning,
    })
  }

  const data = await response.json()
  dispatch(__createFileSuccess(stagedFile.file.name, data))
  return data
}

export const REPLACE_FILE_LOADING = 'REPLACE_FILE_LOADING'
const __replaceFileLoading = fileName => ({
  type: REPLACE_FILE_LOADING,
  fileName,
})

export const REPLACE_FILE_SUCCESS = 'REPLACE_FILE_SUCCESS'
const __replaceFileSuccess = (fileName, payload) => ({
  type: REPLACE_FILE_SUCCESS,
  fileName,
  payload,
})

export const REPLACE_FILE_ERROR = 'REPLACE_FILE_ERROR'
const __replaceFileError = (fileName, error) => ({
  type: REPLACE_FILE_ERROR,
  fileName,
  error,
})

export const replaceFile = (
  studyId,
  stagedFile,
  userRole
) => async (dispatch, getState, { fetch }) => {
  dispatch(__replaceFileLoading(stagedFile.file.name))
  const oldFile = getFilesList(getState()).find(file => file.name === stagedFile.file.name)

  const deleteRes = await fetch(
    `${BASE_URL}/studies/${studyId}/files/${oldFile.id}/`,
    { method: 'DELETE' }
  )

  if (!deleteRes.ok) {
    dispatch(__replaceFileError(stagedFile.file.name, 'Failed to delete file, please try again later.'))
    return null
  }

  const formData = formDataFromStagedFile(stagedFile, userRole)
  const postRes = await fetch(`${BASE_URL}/studies/${studyId}/files/`, {
    method: 'POST',
    body: formData,
  })

  if (!postRes.ok) {
    await processFilePostError(stagedFile, postRes, dispatch, __replaceFileError)
    return null
  }

  const data = await postRes.json()
  dispatch(__replaceFileSuccess(stagedFile.file.name, data))

  return data
}

/**
 * @param {String} studyId - UUID of the study
 * @param {String} userRole - One of 'user', 'manager', 'admin'
 * @returns {Object} - Uploaded file types
 */
export const uploadStagedFiles = (studyId, userRole) => async (dispatch, getState) => {
  // User might be attempting to re-upload staged files if some where successful
  // and some weren't, so clear out current complete files before re-attempting
  const initStagedFiles = getStagedFiles(getState())
  initStagedFiles.forEach(stagedFile => {
    if (stagedFile.isComplete) {
      dispatch(unstageFileForUpload(stagedFile.file.name))
    }
  })

  // Recompute current staged files after potential unstages
  const stagedFiles = getStagedFiles(getState())

  await Promise.all(stagedFiles.map(stagedFile => {
    const action = (stagedFile.willReplaceExistingFile && !isDataFile(stagedFile.file.name))
      ? replaceFile
      : createFile
    return dispatch(action(studyId, stagedFile, userRole))
  }))

  const anyFileUploadFailed = getHasFileUploadFailed(getState())

  const returnVal = {
    hasStagedDataFiles: stagedFiles.some(stagedFile => isDataFile(stagedFile.file.name)),
    anyFileUploadFailed,
  }

  if (stagedFiles.some(stagedFile => !isDataFile(stagedFile.file.name))) {
    dispatch(startFilePolling(studyId))
  }

  window.analytics.track(
    'Submit Upload Files',
    { fileCount: stagedFiles.length }
  )

  return returnVal
}

export const UPDATE_FILE_LOADING = 'UPDATE_FILE_LOADING'
export function updateFileLoading(name) {
  return {
    type: UPDATE_FILE_LOADING,
    payload: { name },
  }
}

export const UPDATE_FILE_SUCCESS = 'UPDATE_FILE_SUCCESS'
export function updateFileSuccess(file) {
  return {
    type: UPDATE_FILE_SUCCESS,
    payload: file,
  }
}

export const UPDATE_FILE_ERROR = 'UPDATE_FILE_ERROR'
export function updateFileError(err) {
  return {
    type: UPDATE_FILE_ERROR,
    error: true,
    payload: err,
  }
}

export const updateFile = (studyId, file) => async (dispatch, getState, { fetch }) => {
  dispatch(updateFileLoading(file.name))

  const response = await fetch(`${BASE_URL}/studies/${studyId}/files/${file.id}/`, {
    method: 'PATCH',
    jsonBody: file,
  })

  if (!response.ok) {
    dispatch(updateFileError(response.statusText))
    return null
  }

  const data = await response.json()
  dispatch(updateFileSuccess(data))
  return data
}

export const DELETE_FILE_LOADING = 'DELETE_FILE_LOADING'
const __deleteFileLoading = () => ({ type: DELETE_FILE_LOADING })

export const DELETE_FILE_SUCCESS = 'DELETE_FILE_SUCCESS'
const __deleteFileSuccess = fileIDs => ({
  type: DELETE_FILE_SUCCESS,
  fileIDs,
})

export const DELETE_FILE_ERROR = 'DELETE_FILE_ERROR'
const __deleteFileError = err => ({
  type: DELETE_FILE_ERROR,
  error: true,
  payload: err,
})

export const deleteFile = (studyId, fileId, fileName) => async (dispatch, getState, { fetch }) => {
  dispatch(__deleteFileLoading(fileId, fileName))

  const response = await fetch(
    `${BASE_URL}/studies/${studyId}/files/${fileId}/`,
    { method: 'DELETE' }
  )

  if (!response.ok) {
    dispatch(__deleteFileError(response.statusText))
    return
  }

  dispatch(__deleteFileSuccess([fileId]))
}

/** Delete all data files on the study */
export const deleteDataset = studyId => async (dispatch, getState, { fetch }) => {
  dispatch(__deleteFileLoading())

  const dataFileIDs = getFilesList(getState()).reduce((acc, file) => {
    if (['data', 'user_data'].includes(file.fileType)) {
      acc.push(file.id)
    }
    return acc
  }, [])

  const response = await fetch(
    `${BASE_URL}/studies/${studyId}/batch-files/`,
    {
      method: 'DELETE',
      jsonBody: dataFileIDs,
    }
  )

  if (!response.ok) {
    dispatch(__deleteFileError(response.statusText))
    return
  }

  dispatch(__deleteFileSuccess(dataFileIDs))
}

export const FETCH_DOCUMENT_LOADING = 'FETCH_DOCUMENT_LOADING'
export function fetchDocumentLoading() {
  return { type: FETCH_DOCUMENT_LOADING }
}

export const FETCH_DOCUMENT_SUCCESS = 'FETCH_DOCUMENT_SUCCESS'
export function fetchDocumentSuccess(document) {
  return {
    type: FETCH_DOCUMENT_SUCCESS,
    payload: document,
  }
}

export const FETCH_DOCUMENT_ERROR = 'FETCH_DOCUMENT_ERROR'
export function fetchDocumentError(err) {
  return {
    type: FETCH_DOCUMENT_ERROR,
    error: true,
    payload: err,
  }
}

/**
 * Fetch individual document details. Used exclusively in Document Viewer
 * @param {String} studyId - ID of the study (i.e. FRE0123)
 * @param {String} fileName - Non-HTML-encoded name of the file being fetched
 * @param {Object} [params] - Optional parameters sent as part of the request
 */
export const fetchDocument = (studyId, fileName, params) => async (
  dispatch,
  getState,
  { fetch }
) => {
  dispatch(fetchDocumentLoading())

  const query = params ? `?${new URLSearchParams(params).toString()}` : ''
  const response = await fetch(
    `${BASE_URL}/studies/${studyId}/documents/${encodeURIComponent(fileName)}/${query}`
  )

  if (!response.ok) {
    dispatch(fetchDocumentError(response.statusText))
    return
  }

  const data = await response.json()
  dispatch(fetchDocumentSuccess(data))
}

export const FILE_DETAILS_FETCH_LOADING = 'FILE_DETAILS_FETCH_LOADING'
const __fileDetailsFetchLoading = () => ({ type: FILE_DETAILS_FETCH_LOADING })

export const FILE_DETAILS_FETCH_SUCCESS = 'FILE_DETAILS_FETCH_SUCCESS'
const __fileDetailsFetchSuccess = file => ({
  type: FILE_DETAILS_FETCH_SUCCESS,
  payload: file,
})

export const FILE_DETAILS_FETCH_ERROR = 'FILE_DETAILS_FETCH_ERROR'
const __fileDetailsFetchError = error => ({
  type: FILE_DETAILS_FETCH_ERROR,
  payload: error,
})

/**
 * Fetch information for a specific file. Used for the Download page, for example.
 * @param {String} studyId - UUID of the target study
 * @param {String} fileId - UUID of the target file
 */
export const fetchFileDetails = (studyId, fileId) => async (dispatch, getState, { fetch }) => {
  dispatch(__fileDetailsFetchLoading())

  const response = await fetch(
    `${BASE_URL}/studies/${studyId}/files/${fileId}/`
  )

  if (!response.ok) {
    dispatch(__fileDetailsFetchError(response.statusText))
    return
  }

  const data = await response.json()
  dispatch(__fileDetailsFetchSuccess(data))
}
